import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./course-details-content.component.scoped.scss";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { UserRoles } from "../../../../../types/userRoles";
import { GlobalContext } from "../../../../../store";
import { BreadcrumbComponent } from "../../../../../components/breadcrumb/breadcrumb.component";

import { services } from "../../../../../services";
import CourseSectionsComponent from "../course-sections/course-sections.component";
import CourseDocumentsComponent from "../course-documents/course-documents.component";
import CourseDetailsHeaderComponent from "../course-details-header/course-details-header.component";
import { CommonService } from "../../../../../services/common/common.service";
import useSections from "../../../hooks/useSections.hook";
import StudentsComponent from "../../../shared/students/students.component";
import { educationRoutes } from "../../../types/education.routes";
import { history } from "react-router-guard";

const CourseDetailsContentComponent = ({ courseId }: { courseId: string }) => {
  const { t } = useTranslation();
  const commonService = useMemo(() => new CommonService(), []);
  const { user, setPageTitle } = useContext(GlobalContext);
  const {
    sections,
    loading: sectionsLoading,
    refetch: refetchSections,
    getSectionFilter,
    onSectionFilterChange,
  } = useSections(Number(courseId));

  const [loading, setLoading] = useState<boolean>(true);
  const [courseDetails, setCourseDetails] = useState<any>(null);
  const [courseStudents, setCourseStudents] = useState<any[]>([]);
  // ---------- Fetch Course Details ----------
  const fetchCourseDetails = useCallback(async () => {
    try {
      setLoading(true);
      const course = await services.courses.get(courseId);
      setCourseDetails(course);
      commonService.setTitle(
        `${course?.title} - ${t("PAGES_HEAD.COURSE_DETAILS")}`,
      );
      setPageTitle(t("PAGES_HEAD.EDUCATION_TITLE"));
    } catch (error) {
      console.error("Error fetching course details:", error);
      setCourseDetails(null);
      history.push(educationRoutes.courses);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, t]);

  const onDeleteDocument = useCallback(async (id: number) => {
    try {
      setCourseDetails((prev: any) => ({
        ...prev,
        documents: prev.documents.filter((document: any) => document.id !== id),
      }));
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  }, []);

  useEffect(() => {
    fetchCourseDetails();
    refetchSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);
  // ---------- Fetch Course Details ----------

  // ---------- Fetch Students ----------
  const fetchStudents = useCallback(
    async (params?: any) => {
      const students = await services.courses.courseStudents(courseId, params);
      setCourseStudents(students);
    },
    [courseId],
  );

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const onStudentsFilterChange = useCallback(
    (filters: any) => {
      fetchStudents(filters);
    },
    [fetchStudents],
  );
  // ---------- Fetch Students ----------

  return (
    <Box className="course__details__content mb-4">
      {loading ? (
        <Skeleton variant="text" width={350} height={18} className="my-4" />
      ) : (
        <BreadcrumbComponent
          items={[
            {
              label: t("EDUCATION_PAGE.EDUCATION"),
              path: "/education",
              separator: "-",
            },
            { label: courseDetails.title },
          ]}
        />
      )}
      {/* Course Details Header */}
      <CourseDetailsHeaderComponent
        courseDetails={courseDetails}
        loading={loading}
        onFetchCourseDetails={fetchCourseDetails}
        onSectionCreated={refetchSections}
        sections={sections}
      />

      {/* Course Details Info */}
      <Box
        className="course__details__info mb-4"
        style={{
          display: "grid",
          gridTemplateColumns:
            courseDetails && courseDetails.documents.length > 0
              ? "1fr 2fr 1.5fr"
              : "1fr 3.5fr",
          gap: "1.5rem",
        }}
      >
        <Box className="couese__image">
          {loading ? (
            <Skeleton
              variant="rect"
              height={200}
              width={300}
              style={{ borderRadius: "10px" }}
            />
          ) : (
            <img
              src={courseDetails.imageURL}
              alt={courseDetails.title}
              title={courseDetails.title}
            />
          )}
        </Box>
        <Box className="d-flex flex-column flex-1">
          {loading ? (
            <>
              <Skeleton
                variant="text"
                height={20}
                width={550}
                className="mb-1"
              />
              <Skeleton
                variant="text"
                height={20}
                width={400}
                className="mb-1"
              />
              <Skeleton
                variant="text"
                height={20}
                width={500}
                className="mb-1"
              />
              <Skeleton
                variant="text"
                height={20}
                width={400}
                className="mb-1"
              />
              <Skeleton
                variant="text"
                height={20}
                width={400}
                className="mb-1"
              />
            </>
          ) : (
            <Typography variant="body1">{courseDetails.description}</Typography>
          )}
        </Box>
        {courseDetails &&
          courseDetails.documents &&
          courseDetails.documents.length > 0 && (
            <CourseDocumentsComponent
              loading={loading}
              onDelete={(id: number) => onDeleteDocument(id)}
              courseId={courseId}
              files={courseDetails.documents}
            />
          )}
      </Box>
      {/* End Course Details Info */}
      {/* Start Course Sections */}
      <Box className="mb-4">
        <CourseSectionsComponent
          sections={sections}
          loading={sectionsLoading}
          onRefetchSections={refetchSections}
          courseId={courseId}
          getSectionFilter={getSectionFilter}
          onSectionFilterChange={onSectionFilterChange}
        />
      </Box>

      {/* Course Statistics */}
      <Box className="global__box__content course__details__statistics mb-4">
        <Box className="course__details__statistics__item">
          <Box className="m-auto">
            <Typography variant="subtitle1" className="font-weight-medium">
              {courseDetails?.statistics?.totalUsers}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t("EDUCATION_PAGE.TOTAL_MEMBERS")}
            </Typography>
          </Box>
        </Box>
        <Box className="course__details__statistics__item">
          <Box className="m-auto">
            <Typography variant="subtitle1" className="font-weight-medium">
              {courseDetails?.statistics?.finishedUsers}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t("EDUCATION_PAGE.COURSE_FINISHED")}
            </Typography>
          </Box>
        </Box>
        <Box className="course__details__statistics__item">
          <Box className="m-auto">
            <Typography variant="subtitle1" className="font-weight-medium">
              {courseDetails?.statistics?.inProgressUsers}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t("EDUCATION_PAGE.COURSE_IN_PROGRESS")}
            </Typography>
          </Box>
        </Box>
      </Box>
      {UserRoles.FULL_ACCESS_ROLES.includes(user.role) && (
        <Box className="mb-4">
          <StudentsComponent
            title={t("EDUCATION_PAGE.COURSE_MEMBERS_FINISHED")}
            users={courseStudents}
            loading={loading}
            onFilterChange={onStudentsFilterChange}
          />
        </Box>
      )}
    </Box>
  );
};
export default CourseDetailsContentComponent;
